import {
  isEmptyString,
} from '@populateui/utils';
import {
  ColumnHeader,
} from 'dtos/columnHeader';
import isNil from 'lodash/isNil';

type TableRow = {
  groupId: string;
  hoverGroupId: string;
  [k: string]: any;
};

export function asArray<T>(value: any): T[] {
  return Array.isArray(value) ? value : [];
}

export function isPlainObject(value: any): value is Record<string, any> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

export function isNilOrEmpty(value: any): boolean {
  const isArray = Array.isArray(value);
  const isObject = isPlainObject(value);
  const isEmpty = isEmptyString(value)
  || (isArray && value.length === 0)
  || (isObject && Object.keys(value).length === 0);

  return isEmpty;
}

export function safeString(value: any, fallback = 'Not set') {
  return isEmptyString(value) ? fallback : String(value);
}

export function getChildren(node: any, key: string | null): any[] {
  let result: any[] = [];
  const value = node?.[key ?? ''];
  if (isEmptyString(key)) {
    result = [];
  } else if (isNil(value)) {
    result = [null];
  } else if (Array.isArray(value)) {
    result = value;
  } else if (isPlainObject(value)) {
    result = [value];
  }

  return result;
}

export function countLeaves(
  node: any,
  headers: readonly ColumnHeader[] | ColumnHeader[],
  depth: number,
): number {
  let result = 1;
  const header = headers[depth];
  if (!isNil(header) && !isNil(header.nextLevelKey)) {
    const children = getChildren(node, header.nextLevelKey);

    result = children.length === 0
      ? 1
      : children.reduce((sum, child) => sum + countLeaves(child, headers, depth + 1), 0);
  }

  return result;
}

let keyCounter = 0;

export function normalizeWildcard(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => {
      const normalized = normalizeWildcard(item);
      if (isPlainObject(normalized)) {
        keyCounter += 1;
        return {
          ...(normalized as Record<string, unknown>),
          uniqueId: String(keyCounter),
        };
      }
      return normalized;
    });
  }
  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [
        k,
        normalizeWildcard(v),
      ]),
    );
  }
  return value;
}

export function buildRows(
  nodes: any[],
  headers: readonly ColumnHeader[],
  depth: number,
  parentRow: TableRow | null,
  rows: TableRow[],
  groupIdPrefix: string,
  inheritedHoverGroupId?: string,
) {
  const header = headers[depth];
  if (!isNil(header)) {
    nodes.forEach((node, idx) => {
      const groupId = `${groupIdPrefix}-${idx}`;
      const hoverGroupId = inheritedHoverGroupId ?? groupId;
      const leafCount = countLeaves(node, headers, depth);
      const rawValue = header.key === '*' ? node : node?.[header?.key ?? ''];
      const cellValue = header.key === '*' ? normalizeWildcard(rawValue) : rawValue;
      const key = `col_${depth}`;

      const baseRow = {
        ...(parentRow ?? { groupId, hoverGroupId }),
        groupId,
        hoverGroupId,
        [key]: {
          value: cellValue,
          rowSpan: leafCount,
        },
      };

      // Terminal column
      if (!header.nextLevelKey) {
        rows.push(baseRow);
        return;
      }

      const children = getChildren(node, header.nextLevelKey);

      // No children → recurse with null to fill remaining columns
      if (children.length === 0) {
        buildRows([null], headers, depth + 1, baseRow, rows, `${groupId}-0`, hoverGroupId);
        return;
      }

      children.forEach((child, childIdx) => {
      // For subsequent children, do NOT repeat the current cell (rowSpan handles it)
        const nextParent = childIdx === 0 ? baseRow : { groupId, hoverGroupId };
        const childPrefix = `${groupId}-${childIdx}`;
        buildRows([child], headers, depth + 1, nextParent, rows, childPrefix, hoverGroupId);
      });
    });
  }
}

export function buildTableRows(rootNodes: any[], headers: ColumnHeader[]): TableRow[] {
  const rows: TableRow[] = [];
  buildRows(rootNodes, headers, 0, null, rows, 'g');
  return rows;
}
